import { Injectable } from '@nestjs/common';
import { CartItemSummary } from '@cart/shared-types';
import { PrismaService } from '@cart/api-data-access';
import { variantLabel } from '@cart/shared-utils';
import { PRODUCTS } from '../products/products.data';
import { StoreContextService } from '../tenancy/store-context.service';

/**
 * Narrows Prisma's free-form optionSnapshot JSON to the string map the label
 * helper expects; anything else is dropped rather than rendered.
 */
function toOptionMap(snapshot: unknown): Record<string, string> | null {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return null;
  const entries = Object.entries(snapshot as Record<string, unknown>)
    .filter(([key, value]) => key.trim() && (typeof value === 'string' || typeof value === 'number'))
    .map(([key, value]) => [key, String(value)] as const);
  return entries.length ? Object.fromEntries(entries) : null;
}

export interface CartProductSnapshot {
  id: string;
  name: string;
  slug: string;
  price: number;
  stock: number;
  active: boolean;
}

/**
 * One purchasable size/option of a product, as the cart needs it.
 *
 * Price lives here rather than on the product because that is the whole point
 * of a variant: 1 L is not reliably twice the price of 500 ml, so the line has
 * to be priced from the thing the shopper actually chose.
 */
export interface CartVariantSnapshot {
  id: string;
  productId: string;
  label: string;
  price: number;
}

export interface CartRecord {
  id: string;
  items: CartItemSummary[];
  couponCode?: string;
  discountTotal?: number;
}

// Mirrors the storefront's existing admin-managed DiscountCode model
// (api/src/app/checkout/checkout.service.ts's resolveDiscount) — the cart's
// "Apply coupon" is a live preview of the exact same codes checkout accepts,
// not a separate discount system.
export interface CouponRecord {
  id: string;
  code: string;
  type: 'percentage' | 'fixed' | 'free_shipping';
  value: number; // basis points (0-10000) for 'percentage'; minor-currency-unit amount for 'fixed'
  minOrderAmount: number | null;
  maxDiscountAmount: number | null;
  customerEligibility: 'all' | 'new_customers' | string;
  stackable: boolean;
  usageLimit: number | null;
  usedCount: number;
  startsOn: Date | null;
  endsOn: Date | null;
  isActive: boolean;
}

export abstract class CartRepository {
  abstract getCart(userId: string): Promise<CartRecord>;
  abstract saveCart(userId: string, cart: CartRecord): Promise<CartRecord>;
  abstract findProduct(productId: string): Promise<CartProductSnapshot | undefined>;
  /** Undefined when the variant does not exist, is inactive, or belongs to another product. */
  abstract findVariant(
    productId: string,
    variantId: string
  ): Promise<CartVariantSnapshot | undefined>;
  abstract findCouponByCode(code: string): Promise<CouponRecord | undefined>;
  /** Count of this customer's prior orders — backs the "new customers only" eligibility rule. */
  abstract countCustomerOrders(userId: string): Promise<number>;
}

// In-memory fallback data — mirrors the demo discount codes seeded into the
// database (prisma/seed.ts's upsertDiscountCodes).
const DEMO_DISCOUNT_CODES: CouponRecord[] = [
  { id: 'disc_demo_save10', code: 'SAVE10', type: 'percentage', value: 1000, minOrderAmount: null, maxDiscountAmount: null, customerEligibility: 'all', stackable: false, usageLimit: null, usedCount: 0, startsOn: null, endsOn: null, isActive: true },
  { id: 'disc_demo_save20', code: 'SAVE20', type: 'percentage', value: 2000, minOrderAmount: null, maxDiscountAmount: null, customerEligibility: 'all', stackable: false, usageLimit: null, usedCount: 0, startsOn: null, endsOn: null, isActive: true },
  { id: 'disc_demo_flat5', code: 'FLAT500', type: 'fixed', value: 50000, minOrderAmount: null, maxDiscountAmount: null, customerEligibility: 'all', stackable: false, usageLimit: null, usedCount: 0, startsOn: null, endsOn: null, isActive: true },
];

@Injectable()
export class InMemoryCartRepository implements CartRepository {
  private readonly carts = new Map<string, CartRecord>();

  async getCart(userId: string): Promise<CartRecord> {
    const cart = this.carts.get(userId);

    if (!cart) {
      return { id: `cart-${userId}`, items: [], discountTotal: 0 };
    }

    return {
      id: cart.id,
      items: cart.items.map((item) => ({ ...item })),
      couponCode: cart.couponCode,
      discountTotal: cart.discountTotal ?? 0,
    };
  }

  async saveCart(userId: string, cart: CartRecord): Promise<CartRecord> {
    this.carts.set(userId, {
      id: cart.id,
      items: cart.items.map((item) => ({ ...item })),
      couponCode: cart.couponCode,
      discountTotal: cart.discountTotal ?? 0,
    });

    return this.getCart(userId);
  }

  async findProduct(productId: string): Promise<CartProductSnapshot | undefined> {
    const product = PRODUCTS.find((item) => item.id === productId);

    if (!product) {
      return undefined;
    }

    return {
      id: product.id,
      name: product.name,
      slug: product.slug,
      price: product.price,
      stock: product.stock,
      active: product.active,
    };
  }

  /**
   * The in-memory catalogue is a fixed demo list with no variants, so there is
   * nothing to resolve. Returning undefined makes the service fall back to
   * product pricing rather than inventing a variant that does not exist.
   */
  async findVariant(): Promise<CartVariantSnapshot | undefined> {
    return undefined;
  }

  async findCouponByCode(code: string): Promise<CouponRecord | undefined> {
    const coupon = DEMO_DISCOUNT_CODES.find((c) => c.code === code.toUpperCase());
    return coupon ? { ...coupon } : undefined;
  }

  async countCustomerOrders(): Promise<number> {
    return 0; // in-memory demo mode — no real order history to check
  }
}

@Injectable()
export class PrismaCartRepository implements CartRepository {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext?: StoreContextService
  ) {}

  async getCart(userId: string): Promise<CartRecord> {
    const cart = await this.findOrCreateCart(userId);
    const items = await this.prisma.cartItem.findMany({
      where: { storeId: this.storeId(), cartId: cart.id },
      orderBy: { createdAt: 'asc' },
    });
    const products = await this.prisma.product.findMany({
      where: {
        storeId: this.storeId(),
        id: { in: items.map((item) => item.productId) },
      },
      include: {
        inventory: {
          select: { quantity: true },
        },
        images: {
          orderBy: { sortOrder: 'asc' },
          take: 1,
          select: { url: true },
        },
      },
    });
    const productsById = new Map(products.map((product) => [product.id, product]));

    // Labels are read back from the variants rather than stored on the line:
    // a merchant who corrects "1 Ltr" to "1 L" should not leave every cart in
    // the store showing the old wording.
    const variantIds = items.map((item) => item.variantId).filter((id): id is string => !!id);
    const variants = variantIds.length
      ? await this.prisma.productVariant.findMany({
          where: { id: { in: variantIds }, storeId: this.storeId() },
          select: {
            id: true,
            title: true,
            unitQuantity: true,
            unitOfMeasure: true,
            optionSnapshot: true,
          },
        })
      : [];
    const labelsById = new Map(
      variants.map((variant) => [
        variant.id,
        variantLabel({
          options: toOptionMap(variant.optionSnapshot),
          unitQuantity: variant.unitQuantity,
          unitOfMeasure: variant.unitOfMeasure,
          title: variant.title,
        }),
      ])
    );

    return {
      id: cart.token,
      items: items.map((item) => {
        const product = productsById.get(item.productId);

        return {
          id: item.id,
          productId: item.productId,
          productName: item.title,
          productSlug: item.sku ?? product?.slug ?? item.productId,
          productImageUrl: product?.images?.[0]?.url,
          unitPrice: item.unitPrice,
          quantity: item.quantity,
          lineTotal: item.lineTotal,
          stock: product?.inventory?.quantity ?? 0,
          variantId: item.variantId,
          variantLabel: item.variantId ? labelsById.get(item.variantId) ?? null : null,
        };
      }),
      couponCode: cart.couponCode ?? undefined,
      discountTotal: cart.discountTotal,
    };
  }

  async saveCart(userId: string, cart: CartRecord): Promise<CartRecord> {
    const persisted = await this.findOrCreateCart(userId);

    await this.prisma.$transaction([
      this.prisma.cartItem.deleteMany({
        where: { storeId: this.storeId(), cartId: persisted.id },
      }),
      this.prisma.cart.update({
        where: { id: persisted.id },
        data: {
          subtotal: cart.items.reduce((sum, item) => sum + item.lineTotal, 0),
          couponCode: cart.couponCode ?? null,
          discountTotal: cart.discountTotal ?? 0,
        },
      }),
      ...cart.items.map((item) =>
        this.prisma.cartItem.create({
          data: {
            id: item.id,
            storeId: this.storeId(),
            cartId: persisted.id,
            productId: item.productId,
            variantId: item.variantId ?? null,
            title: item.productName,
            sku: item.productSlug,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            lineTotal: item.lineTotal,
          },
        })
      ),
    ]);

    return this.getCart(userId);
  }

  async findProduct(productId: string): Promise<CartProductSnapshot | undefined> {
    const product = await this.prisma.product.findFirst({
      where: { id: productId, storeId: this.storeId() },
      include: {
        inventory: {
          select: { quantity: true },
        },
      },
    });

    if (!product) {
      return undefined;
    }

    return {
      id: product.id,
      name: product.name,
      slug: product.slug,
      price: product.price,
      stock: product.inventory?.quantity ?? 0,
      active: product.active,
    };
  }

  async findVariant(
    productId: string,
    variantId: string
  ): Promise<CartVariantSnapshot | undefined> {
    const variant = await this.prisma.productVariant.findFirst({
      // productId is part of the filter, not just the id: a variant id from
      // another product would otherwise price this line from someone else's
      // catalogue entry.
      where: { id: variantId, productId, storeId: this.storeId(), isActive: true },
      select: {
        id: true,
        productId: true,
        title: true,
        price: true,
        unitQuantity: true,
        unitOfMeasure: true,
        optionSnapshot: true,
      },
    });

    if (!variant) return undefined;

    return {
      id: variant.id,
      productId: variant.productId,
      label: variantLabel({
        options: toOptionMap(variant.optionSnapshot),
        unitQuantity: variant.unitQuantity,
        unitOfMeasure: variant.unitOfMeasure,
        title: variant.title,
      }),
      price: variant.price,
    };
  }

  async findCouponByCode(code: string): Promise<CouponRecord | undefined> {
    const discount = await this.prisma.discountCode.findFirst({
      where: { storeId: this.storeId(), code: code.toUpperCase() },
    });

    if (!discount) return undefined;

    return {
      id: discount.id,
      code: discount.code,
      type: discount.type as CouponRecord['type'],
      value: discount.value,
      minOrderAmount: discount.minOrderAmount,
      maxDiscountAmount: discount.maxDiscountAmount,
      customerEligibility: discount.customerEligibility,
      stackable: discount.stackable,
      usageLimit: discount.usageLimit,
      usedCount: discount.usedCount,
      startsOn: discount.startsOn,
      endsOn: discount.endsOn,
      isActive: discount.isActive,
    };
  }

  async countCustomerOrders(userId: string): Promise<number> {
    return this.prisma.order.count({
      where: { storeId: this.storeId(), customerId: userId },
    });
  }

  private async findOrCreateCart(userId: string) {
    const token = this.cartToken(userId);
    const existing = await this.prisma.cart.findUnique({
      where: { token },
    });

    if (existing) {
      return existing;
    }

    return this.prisma.cart.create({
      data: {
        id: token,
        storeId: this.storeId(),
        customerId: userId,
        token,
        currency: 'INR',
      },
    });
  }

  private cartToken(userId: string): string {
    return `cart-${userId}-${this.storeId()}`;
  }

  private storeId(): string {
    return this.storeContext?.storeId() ?? 'store_demo';
  }
}
